/**
 * `Rule` — the chalk-masked 1px printed hairline. `docs/design-direction.md` §6.10.
 *
 * All separation in this system lives in 1px rules; nothing is separated by a shadow
 * (§5.5). The mark itself — the hairline plus the grain dropout mask that wears its
 * ends away — is the global `.rule` recipe in `styles/texture.css`; this component is
 * the semantic wrapper that renders it as an `<hr>`, so the separator is in the
 * accessibility tree instead of being an anonymous div.
 *
 * Three of §5.4's line tiers can exist as a free-standing rule. `structure` is the
 * workhorse `--border` hairline (row dividers, panel edges, module edges);
 * `emphasis` steps up to `--border-strong` and is what every 4th ledger row and every
 * sticky-header bottom edge uses — bar lines, which give a scroll an audible metre
 * with zero decoration; `pending` is the 1px dashed form for queued-but-unplayed and
 * not-yet-downloaded state. The `active` 2px `--border-active` tier is deliberately
 * absent: it is always a border on a specific component (a selected chip, a focused
 * input, the now-playing row's 3px left rule), never a rule of its own.
 *
 * The downbeat rule — the only heading decoration in the system — is `DownbeatRule`.
 */

import type { JSX } from 'react';

import styles from './primitives.module.css';

/**
 * Join class names, dropping anything absent.
 *
 * It lives here rather than in a module of its own because T26's file list is closed:
 * one file, one owner, and a `cx.ts` would be a file nobody owns. `Rule` is its home
 * because the 1px rule is the smallest mark in the system, and every other primitive
 * imports `cx` from here.
 *
 * The `undefined` branch is not defensive: Vite types a CSS module as an index
 * signature, so `styles.foo` is `string | undefined` under `noUncheckedIndexedAccess`.
 */
export function cx(...parts: readonly (string | false | null | undefined)[]): string {
  return parts.filter((part): part is string => typeof part === 'string' && part !== '').join(' ');
}

/** Which of §5.4's line tiers to print. */
export type RuleTier = 'structure' | 'emphasis' | 'pending';

export interface RuleProps {
  /** §5.4 line tier. Default `structure` — the workhorse `--border` hairline. */
  readonly tier?: RuleTier;
  readonly className?: string | undefined;
}

/** A single printed hairline. */
export function Rule({ tier = 'structure', className }: RuleProps): JSX.Element {
  return (
    <hr
      className={cx(
        'rule',
        tier === 'emphasis' && styles.strong,
        tier === 'pending' && styles.dash,
        className,
      )}
      data-tier={tier}
    />
  );
}
